import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, Plus, Trash2, Mail, MessageCircle, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FieldWrapper } from '@/components/ui/field-wrapper';
import { senderProfilesApi } from '@/features/sender-profiles/api';
import { EVENT_TYPES, templatesApi, type NotificationTemplate, type TemplateInput } from './api';

export function TemplatesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TemplateInput | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const templatesQuery = useQuery({ queryKey: ['templates'], queryFn: () => templatesApi.list() });
  const contextQuery = useQuery({ queryKey: ['template-context'], queryFn: () => templatesApi.context() });
  const profilesQuery = useQuery({ queryKey: ['sender-profiles'], queryFn: () => senderProfilesApi.list() });

  const saveMutation = useMutation({
    mutationFn: (input: TemplateInput) =>
      input.id
        ? templatesApi.update(input.id, {
            subject: input.subject,
            body: input.body,
            senderProfileId: input.senderProfileId,
            isActive: input.isActive,
          })
        : templatesApi.create(input),
    onSuccess: () => {
      toast.success('Template saved.');
      setEditing(null);
      setPreviewHtml(null);
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed — check the template syntax.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: () => {
      toast.success('Override removed — falls back to default.');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      templatesApi.preview(editing!.body, editing!.subject ?? null, editing!.channel),
    onSuccess: (res) => setPreviewHtml(res.rendered),
    onError: (e: Error) => toast.error(e.message || 'Preview failed.'),
  });

  // Live-ish preview: refresh when the editor opens.
  useEffect(() => {
    setPreviewHtml(null);
  }, [editing?.id, editing?.channel]);

  const templates = templatesQuery.data ?? [];
  const variables = contextQuery.data?.variables ?? [];
  const channelProfiles = (profilesQuery.data ?? []).filter((p) => p.Channel === editing?.channel);

  const updateField = (key: keyof TemplateInput, value: any) => {
    setEditing(prev => prev ? { ...prev, [key]: value } : prev);
  };

  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FileCode size={20} className="text-[#008ED3]" />
          Notification Templates
        </h1>
        <Button
          onClick={() =>
            setEditing({ eventType: 'task_assigned', channel: 'Email', body: '', isActive: true })
          }
          className="gap-2"
        >
          <Plus size={16} /> New template
        </Button>
      </div>

      {templatesQuery.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[#008ED3]" />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50 text-gray-700">
              <TableHead className="px-4 py-3 font-semibold">Event</TableHead>
              <TableHead className="px-4 py-3 font-semibold">Channel</TableHead>
              <TableHead className="px-4 py-3 font-semibold">Scope</TableHead>
              <TableHead className="px-4 py-3 font-semibold">Subject</TableHead>
              <TableHead className="px-4 py-3 font-semibold w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.TemplateID} className="group">
                <TableCell className="px-4 py-3 font-medium text-gray-900">{t.EventType}</TableCell>
                <TableCell className="px-4 py-3">
                  <div className="flex items-center gap-2 text-gray-600">
                    {t.Channel === 'Email' ? <Mail size={14} className="text-blue-500" /> : <MessageCircle size={14} className="text-green-500" />}
                    {t.Channel}
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3">
                  <Badge
                    className={`border-0 shadow-none font-medium ${
                      t.TenantOrganizationID
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {t.TenantOrganizationID ? 'Tenant' : 'Global'}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-gray-500 max-w-[260px] truncate">
                  {t.Subject ?? '—'}
                </TableCell>
                <TableCell className="px-4 py-3 text-right whitespace-nowrap">
                  <Button
                    variant="ghost_blue"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() =>
                      setEditing({
                        id: t.TemplateID,
                        eventType: t.EventType,
                        channel: t.Channel,
                        subject: t.Subject,
                        body: t.Body,
                        senderProfileId: t.SenderProfileID,
                        isActive: Boolean(t.IsActive),
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                    onClick={() => deleteMutation.mutate(t.TemplateID)}
                    title="Remove override"
                  >
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!templatesQuery.isLoading && templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="px-4 py-12 text-center text-gray-400">
                  No DB templates yet — events render the built-in file templates.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* editor dialog */}
      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-[1000px] gap-0 p-0 overflow-hidden">
          <DialogHeader className="p-6 border-b">
            <DialogTitle>
              {editing?.id ? `Edit template — ${editing.eventType}` : 'New template override'}
            </DialogTitle>
            <DialogDescription>
              Customize the notification content using Jinja2 templates and system variables.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* left column: editor */}
              <div className="p-6 space-y-4 border-r overflow-y-auto max-h-[70vh]">
                {!editing.id && (
                  <div className="grid grid-cols-2 gap-4">
                    <FieldWrapper label="Event type" required>
                      <Select
                        value={editing.eventType}
                        onValueChange={val => updateField('eventType', val)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EVENT_TYPES.map(ev => <SelectItem key={ev} value={ev}>{ev}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FieldWrapper>
                    <FieldWrapper label="Channel" required>
                      <Select
                        value={editing.channel}
                        onValueChange={val => updateField('channel', val as 'Email' | 'WhatsApp')}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldWrapper>
                  </div>
                )}

                {editing.channel === 'Email' && (
                  <FieldWrapper label="Subject (Jinja2)">
                    <Input
                      value={editing.subject ?? ''}
                      onChange={e => updateField('subject', e.target.value)}
                      placeholder="e.g. New Task Assigned: {{ task_title }}"
                    />
                  </FieldWrapper>
                )}

                <FieldWrapper
                  label={`Body (${editing.channel === 'Email' ? 'HTML fragment' : 'Plain text'}, Jinja2)`}
                  required
                >
                  <Textarea
                    value={editing.body}
                    onChange={e => updateField('body', e.target.value)}
                    className="font-mono text-[12px] min-h-[300px]"
                    placeholder={editing.channel === 'Email' ? '<p>Hello {{ user_name }},</p>...' : 'Hello {{ user_name }}, ...'}
                  />
                </FieldWrapper>

                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Available Variables
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {variables.map((v) => (
                      <button
                        key={v}
                        onClick={() => updateField('body', `${editing.body}{{ ${v} }}`)}
                        className="px-2 py-0.5 text-[11px] font-mono bg-gray-100 hover:bg-[#EBF6FD] hover:text-[#008ED3] text-gray-600 rounded transition-colors"
                      >
                        {`{{ ${v} }}`}
                      </button>
                    ))}
                  </div>
                </div>

                <FieldWrapper label="Sender profile override">
                  <Select
                    value={editing.senderProfileId ?? 'default'}
                    onValueChange={val => updateField('senderProfileId', val === 'default' ? null : val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Channel default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Channel default</SelectItem>
                      {channelProfiles.map(p => (
                        <SelectItem key={p.SenderProfileID} value={p.SenderProfileID}>
                          {p.ProfileName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldWrapper>
              </div>

              {/* right column: preview */}
              <div className="p-6 bg-gray-50 flex flex-col h-full overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[12px] font-semibold text-gray-600">Live Preview</label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 bg-white"
                    onClick={() => previewMutation.mutate()}
                    disabled={!editing.body || previewMutation.isPending}
                  >
                    {previewMutation.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <>
                        <Eye size={14} className="mr-1.5" />
                        Render
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-inner min-h-[400px]">
                  {previewHtml ? (
                    editing.channel === 'Email' ? (
                      <iframe title="preview" srcDoc={previewHtml} className="w-full h-full" />
                    ) : (
                      <div className="p-4 text-[13px] font-mono whitespace-pre-wrap text-gray-700">
                        {previewHtml}
                      </div>
                    )
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8 text-gray-400">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <Eye size={20} />
                      </div>
                      <p className="text-sm">Click Render to see how your template looks with sample data.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="p-6 border-t bg-gray-50/50">
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => editing && saveMutation.mutate(editing)}
              disabled={!editing?.body || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save template override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
